import React, { useRef, useState, useMemo, useEffect, useCallback, Suspense } from 'react';
import * as THREE from 'three';
import { Canvas } from '@react-three/fiber';

// Gallery modules
import './gallery/constants';                           // side-effects: patch THREE, preload textures
import { ARTWORKS } from '../data/artworks';
import { FOLLOW_HEIGHT, FOLLOW_DIST } from './gallery/constants';
import GalleryRoom, { StarField }      from './gallery/GalleryRoom';
import ArtworkFrame                    from './gallery/ArtworkFrame';
import ArtworkViewingZone              from './gallery/ArtworkViewingZone';
import PortalTextAlternator            from './gallery/PortalTextAlternator';
import HebrewAvatar                    from './gallery/HebrewAvatar';
import GuestBookAltar                  from './gallery/GuestBookAltar';
import PlayerController                from './gallery/PlayerController';
import { StaticJoystick, MobileLookZone, GalleryHUD, DesktopKeyboardHint } from './gallery/controls';

// ──────────────────────────────────────────────
// Main Gallery Scene — ~130 lines, all logic delegated to modules
// ──────────────────────────────────────────────
export default function GalleryScene({
  selectedArtwork, onCameraArrived, isDetailMode, onArtworkSelect,
  language, onReturnToStart, onOpenGuestbook, pendingArtworkId, onEnterSecondRoom,
  skipIntro, overlayOpen, galleryReset,
}) {
  const isMobile = useMemo(() => window.innerWidth < 768 || 'ontouchstart' in window, []);
  const SPAWN_Z  = isMobile ? 30 : 24;
  const isFr     = language === 'fr';
  const isHe     = language === 'he';

  const avatarRef    = useRef({ x: 0, z: SPAWN_Z, yaw: 0 });
  const joystickRef  = useRef({ x: 0, y: 0 });
  const lookRef      = useRef({ dx: 0, dy: 0 });

  const [hoveredId,       setHoveredId]       = useState(null);
  const [isNearGuestbook, setIsNearGuestbook] = useState(false);
  const [nearArtworkId,   setNearArtworkId]   = useState(null);
  const [isNearPortal,    setIsNearPortal]    = useState(false);
  const [showPortalSoon,  setShowPortalSoon]  = useState(false);
  const [showVisitHint,   setShowVisitHint]   = useState(false);
  const [sceneReady,      setSceneReady]      = useState(false);
  const [showLoader,      setShowLoader]      = useState(true);


  useEffect(() => {
    const t1 = setTimeout(() => setShowVisitHint(true),  200);
    const t2 = setTimeout(() => setShowVisitHint(false), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Signaler que la scène est prête dès que le WebGL est initialisé
  const handleCanvasCreated = useCallback(() => {
    setTimeout(() => {
      setSceneReady(true);
      setTimeout(() => setShowLoader(false), 750);
    }, 400);
  }, []);

  // Réinitialiser la position de l'avatar (bouton Home)
  useEffect(() => {
    if (!galleryReset) return;
    avatarRef.current   = { x: 0, z: SPAWN_Z, yaw: 0 };
    joystickRef.current = { x: 0, y: 0 };
    lookRef.current     = { dx: 0, dy: 0 };
  }, [galleryReset]);

  const handleReturnToStart = useCallback(() => {
    avatarRef.current.x = 0; avatarRef.current.z = SPAWN_Z; avatarRef.current.yaw = 0;
    if (onReturnToStart) onReturnToStart();
  }, [onReturnToStart]);

  const handleNearPortal = useCallback((isNear) => {
    setIsNearPortal(isNear);
    if (isNear) setShowPortalSoon(true);
  }, []);

  return (
    <div className="gallery-container" style={{ width: '100vw', height: '100vh' }} data-testid="gallery-scene">

      {/* Overlay de chargement initial */}
      {showLoader && (
        <div
          className={`gallery-loader${sceneReady ? ' gallery-loader--out' : ''}`}
          data-testid="gallery-loading-overlay"
        >
          <div className="gallery-loader-brand">SHIRATSEVA</div>
          <div className="gallery-loader-he">שירת צבע</div>
          <div className="gallery-loader-dots">
            <span /><span /><span />
          </div>
        </div>
      )}

      {showVisitHint && (
        <div className="gallery-visit-intro" data-testid="gallery-visit-hint">
          <span className="visit-intro-text">
            {isHe ? 'בקר בגלריה' : isFr ? 'Visiter la galerie' : 'Visit the Gallery'}
          </span>
          <span className="visit-intro-arrow">↓</span>
        </div>
      )}

      <Canvas
        camera={{ position: [0, FOLLOW_HEIGHT, SPAWN_Z + FOLLOW_DIST], fov: isMobile ? 68 : 62, near: 0.1, far: isMobile ? 90 : 130 }}
        gl={{ antialias: true, alpha: false, powerPreference: isMobile ? 'default' : 'high-performance', toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.1 }}
        dpr={[1, 1.5]}
        style={{ width: '100%', height: '100%' }}
        onCreated={handleCanvasCreated}
        data-testid="gallery-canvas"
      >
        <Suspense fallback={null}>
          <fog attach="fog" args={['#EEE8DC', isMobile ? 30 : 40, isMobile ? 75 : 88]} />
          {/* Ambient réduit → contrastes galerie premium. Spots et lumières d'œuvres dominent. */}
          <ambientLight intensity={isMobile ? 0.44 : 0.36} color="#FFF6EE" />
          <hemisphereLight args={['#F0EAD8', '#C8B898', isMobile ? 0.22 : 0.14]} />

          <GalleryRoom isMobile={isMobile} />
          {!isMobile && <StarField />}

          {ARTWORKS.map(aw => (
            <ArtworkFrame
              key={aw.id} artwork={aw}
              onSelect={onArtworkSelect} onHover={setHoveredId}
              isHovered={hoveredId === aw.id} isNear={nearArtworkId === aw.id}
              isMobile={isMobile}
            />
          ))}
          {ARTWORKS.map(aw => (
            <ArtworkViewingZone
              key={`zone-${aw.id}`} artwork={aw}
              isNear={nearArtworkId === aw.id}
              onSelect={onArtworkSelect} isMobile={isMobile}
            />
          ))}

          <PortalTextAlternator isNearPortal={isNearPortal} />
          <HebrewAvatar avatarRef={avatarRef} />
          <GuestBookAltar onOpen={onOpenGuestbook} isNear={isNearGuestbook} />

          <PlayerController
            avatarRef={avatarRef}
            selectedArtwork={selectedArtwork}
            onCameraArrived={onCameraArrived}
            isDetailMode={isDetailMode}
            joystickRef={joystickRef}
            lookRef={lookRef}
            onNearGuestbook={setIsNearGuestbook}
            onNearArtworkChange={setNearArtworkId}
            onNearPortal={handleNearPortal}
            onExitDetail={onReturnToStart}
            isMobile={isMobile}
            skipIntro={skipIntro}
          />
        </Suspense>
      </Canvas>

      <GalleryHUD
        isDetailMode={isDetailMode} isMobile={isMobile}
        onReturnToStart={handleReturnToStart} language={language}
        isNearGuestbook={isNearGuestbook} onOpenGuestbook={onOpenGuestbook}
        nearArtworkId={nearArtworkId}
      />

      {showPortalSoon && (
        <div className="portal-soon-overlay" data-testid="portal-soon-overlay">
          <div className="portal-soon-content">
            <div className="portal-soon-star">✦</div>
            <span className="portal-soon-badge">חדר הסתכלות · Salle de Contemplation</span>
            <h2 className="portal-soon-title">
              {isHe ? 'חדר ההסתכלות' : isFr ? 'Salle de Contemplation' : 'Contemplation Chamber'}
            </h2>
            <div className="portal-coming-soon-badge" data-testid="portal-coming-soon-badge">
              {isHe ? 'בקרוב' : isFr ? 'Prochainement' : 'Coming Soon'}
            </div>
            <p className="portal-soon-text">
              {isHe
                ? 'מרחב קדוש זה נמצא בהכנה.\nשש יצירות. שתיקה עמוקה. חוויה ייחודית.'
                : isFr
                  ? 'Cette salle sacrée est en cours de préparation.\nSix œuvres. Un silence profond. Une expérience unique.'
                  : 'This sacred space is being prepared.\nSix artworks. A deep silence. A unique experience.'}
            </p>
            <button className="portal-soon-btn" onClick={() => { setShowPortalSoon(false); handleReturnToStart(); }} data-testid="portal-soon-close-button">
              {isHe ? '← חזרה לגלריה' : isFr ? '← Retourner à la galerie' : '← Return to Gallery'}
            </button>
          </div>
        </div>
      )}

      {isMobile && !isDetailMode && !showPortalSoon && (
        <>
          <StaticJoystick joystickRef={joystickRef} isDetailMode={!!(isDetailMode || overlayOpen)} />
          <MobileLookZone lookRef={lookRef} isDetailMode={!!(isDetailMode || overlayOpen)} active={!overlayOpen} />
        </>
      )}

      {/* ── Touches clavier (Desktop uniquement) ── */}
      {!isMobile && !showPortalSoon && (
        <DesktopKeyboardHint isDetailMode={!!(isDetailMode || overlayOpen)} />
      )}
    </div>
  );
}
